/*
 UI COMPONENT: Classic Slider
 ---------------------------------------------------
 Horizontal scroll slider with prev/next navigation.
 Steps one slide at a time and lets CSS scroll-snap align the result,
 so it stays correct with any gap, slide width or snap alignment.
 Supports optional bullet pagination (auto-generated).
 Syncs state on manual swipe/scroll.

 Targets
 ---------------------------------------------------
 slides    — scrollable container wrapping all slides (children are the slides)
 prev      — previous button (auto-disabled at start)
 next      — next button (auto-disabled at end)
 bullets   — (optional) container for bullet navigation, auto-populated with buttons

 Actions
 ---------------------------------------------------
 slider--classic#prev  — scroll to previous slide
 slider--classic#next  — scroll to next slide

 Usage
 ---------------------------------------------------
 <div data-controller="slider--classic">
   <div data-slider--classic-target="slides">
     <div>...</div>
     <div>...</div>
   </div>
   <button data-slider--classic-target="prev" data-action="slider--classic#prev">Prev</button>
   <button data-slider--classic-target="next" data-action="slider--classic#next">Next</button>
   <div data-slider--classic-target="bullets"></div>  <!-- optional -->
 </div>
 */
import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['slides', 'prev', 'next', 'bullets']

  scroller
  buttons = null

  connect() {
    this.scroller = this.slidesTarget
    if(!this.scroller.children.length) return

    this.scroller.addEventListener('scroll', () => this.#sync(), { passive: true })

    if(this.hasBulletsTarget) {
      this.#createSliderNav(this.scroller.children.length)
    }

    this.scroller.scrollTo({ left: 0 })
    this.#sync()
  }

  prev() {
    this.scroller?.scrollBy({ left: -this.#step, behavior: 'smooth' })
  }

  next() {
    this.scroller?.scrollBy({ left: this.#step, behavior: 'smooth' })
  }

  // Slide-to-slide distance (width + gap) read from the DOM. Getter, deliberately not
  // cached to keep things simple: measured on demand, always right across breakpoints.
  get #step() {
    const [first, second] = this.scroller.children
    return second ? second.offsetLeft - first.offsetLeft : first.offsetWidth
  }

  //----------------------------------------------------
  // Synchronisation Scroller/Navs
  //----------------------------------------------------
  #sync() {
    const { scrollLeft, clientWidth, scrollWidth } = this.scroller
    this.#syncNav(scrollLeft, clientWidth, scrollWidth)
    if(this.hasBulletsTarget) this.#syncBullets(scrollLeft)
  }

  #syncNav(scrollLeft, clientWidth, scrollWidth) {
    this.prevTarget.disabled = scrollLeft <= 1
    this.nextTarget.disabled = scrollLeft + clientWidth >= scrollWidth - 1
  }

  #syncBullets(scrollLeft) {
    const active = Math.round(scrollLeft / this.#step)
    this.buttons.forEach((button, index) => button.toggleAttribute('active', index === active))
  }

  //----------------------------------------------------
  // Navigations (Prev/next = Bullets)
  //----------------------------------------------------
  #goTo(index) {
    const slide = this.scroller.children[index]
    if(!slide) return

    this.scroller.scrollTo({
      left: slide.offsetLeft - this.scroller.children[0].offsetLeft,
      behavior: 'smooth'
    })
  }

  #buttonHandler(button, index) {
    button.addEventListener('click', () => this.#goTo(index))
  }

  #createButton(label, active=false) {
    const button = document.createElement('button')
    if (active) button.setAttribute('active', '')

    const span = document.createElement('span')
    span.classList.add('sr-only')
    span.textContent = label
    button.appendChild(span)

    return button;
  }

  #createSliderNav(count=1) {
    let index = 0;
    while (index < count) {
      const button = this.#createButton(index, index === 0)
      this.bulletsTarget.appendChild(button)
      this.#buttonHandler(button, index)
      index++
    }
    this.buttons = this.bulletsTarget.querySelectorAll('button')
  }
}
